import React, { CSSProperties, MouseEvent, PropsWithChildren } from "react";
import DisconnectOutlined from "@ant-design/icons/DisconnectOutlined";
import { useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import { AccountObjectType } from "@shared/features/settings/types";
import { ClientStatus } from "@shared/types";
import { TooltipPlacement } from "antd/es/tooltip";
import Tooltip from "common/Tooltips/Tooltip";
import Text from "common/Typography/Text";

interface InactiveAccountIconProps extends PropsWithChildren {
  accountId?: number;
  isInvalid?: boolean;
  isInactive?: boolean;
  accountStatus?: ClientStatus;
  objectType?: AccountObjectType;
  invalidColor?: string;
  hideIcon?: boolean;
  validColor?: string;
  style?: CSSProperties;
  placement?: TooltipPlacement;
}

const InactiveAccountIcon = ({
  accountId,
  isInvalid: propsIsInvalid,
  isInactive: propsIsInactive,
  objectType,
  invalidColor: propsInvalidColor,
  validColor: propsValidColor,
  hideIcon,
  children,
  style,
  placement,
}: InactiveAccountIconProps) => {
  const theme = useTheme();
  const { getInvalidAccountData } = useInvalidAccount();

  const accountData = getInvalidAccountData(accountId);
  const isInvalid = propsIsInvalid ?? accountData?.isInvalid;
  const isInactive = propsIsInactive ?? accountData?.isInactive;
  const invalidColor = propsInvalidColor ?? accountData?.color;
  const validColor = propsValidColor ?? theme.colors.primaryText;
  const showTooltip = isInvalid || isInactive;

  const content = (
    <Flex
      $justifyContent="flex-start"
      style={{
        color: isInvalid || isInactive ? invalidColor : validColor,
        ...style,
      }}
      onClick={(e: MouseEvent) =>
        showTooltip ? e.stopPropagation() : undefined
      }
    >
      {children}
      {(isInvalid || isInactive) && !hideIcon && (
        <DisconnectOutlined style={{ color: invalidColor, marginLeft: 6 }} />
      )}
    </Flex>
  );

  if (showTooltip) {
    let title: string;
    let validName = isInactive ? "active" : "valid";
    if (objectType === AccountObjectType.TRADE) {
      title = `Trade no longer managed - this trade was created with an exchange account that is no longer ${validName}, and therefore orders placed are no longer being updated`;
    } else if (objectType === AccountObjectType.BOT) {
      title = `This bot was created with an exchange account that is no longer ${validName}, new trades will not open and its existing trades are no longer managed.`;
    } else {
      title = isInactive
        ? "This account was deactivated"
        : "This account's API keys are no longer valid.";
    }
    return (
      <Tooltip
        title={<Text style={{ color: "inherit" }}>{title}</Text>}
        placement={placement}
        showMobileBottomDrawer={true}
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};

export default InactiveAccountIcon;
